import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useAnalyticsService from "../../../services/AnalyticsService";
import { useUrlDateRange } from "../../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../../components/DateRangeToolbar";
import { EnterpriseStreamDetail } from "../../../types/Analytics";
import Ghost from "../../../components/Ghost";
import PageTitle from "../../../components/PageTitle";
import BackButton from "../../../components/BackButton";
import LoadingEllipsis from "../../../components/LoadingEllipsis";

const minutesToHours = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = (minutes % 60).toFixed(0);

  return `${hours}h ${mins}m`;
};

export default function AdminAnalyticsEnterpriseStreamDetailPage() {
  const { experienceId } = useParams();
  const { getEnterpriseStreamDetail } = useAnalyticsService();
  const { startDate, endDate, setRange } = useUrlDateRange(7);
  const [isLoading, setIsLoading] = useState(false);
  const [detail, setDetail] = useState<EnterpriseStreamDetail | null>(null);

  const getData = async () => {
    if (!startDate || !endDate || !experienceId || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await getEnterpriseStreamDetail(experienceId, startDate, endDate);
      setDetail(result);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!startDate || !endDate || !experienceId) return;

    getData();
  }, [startDate, endDate, experienceId]);

  return (
    <>
      <section className="flex flex-col gap-2">
        <div className="flex flex-row">
          <BackButton />
          <PageTitle className="text-4xl">
            {detail ? (detail.displayName || detail.experienceId) : `Loading`} {!detail && <LoadingEllipsis />}
          </PageTitle>
        </div>
        {startDate && endDate && (
          <DateRangeToolbar
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
          />
        )}
      </section>
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {isLoading && (
          <>
            <Ghost additionalClasses="w-full h-24" />
            <Ghost additionalClasses="w-full h-24" />
            <Ghost additionalClasses="w-full h-24" />
            <Ghost additionalClasses="w-full h-24" />
          </>
        )}
        {!isLoading && detail && (
          <>
            <div className="card p-4 flex flex-col gap-1">
              <span className="text-sm opacity-75">Total Data Sessions</span>
              <span className="text-2xl">{detail.totalDataSessionCount}</span>
            </div>
            <div className="card p-4 flex flex-col gap-1">
              <span className="text-sm opacity-75">Total Data Session Minutes</span>
              <span className="text-2xl">{minutesToHours(detail.totalDataSessionMinutes)}</span>
            </div>
            <div className="card p-4 flex flex-col gap-1">
              <span className="text-sm opacity-75">Total Streaming Sessions</span>
              <span className="text-2xl">{detail.totalStreamingSessionCount}</span>
            </div>
            <div className="card p-4 flex flex-col gap-1">
              <span className="text-sm opacity-75">Total Streaming Session Minutes</span>
              <span className="text-2xl">{minutesToHours(detail.totalStreamingSessionMinutes)}</span>
            </div>
          </>
        )}
      </section>
    </>
  );
}
